/**
 * Canonical form of an email address: trimmed and lower-cased.
 *
 * Addresses are an identity key here — `UserEmail.email` is globally unique and
 * is what login, invites and password reset match on. Matching them
 * case-sensitively means `Alice@example.com` and `alice@example.com` are two
 * different people, which is not what anybody typing their own address
 * believes. In practice it produced duplicate accounts: register with one
 * casing, sign in with another, and the lookup finds nothing.
 *
 * RFC 5321 does say the local part is case-sensitive and only the domain is
 * not. No mail provider in use actually honours that, and the standard itself
 * discourages relying on it, so the whole address is folded — the alternative
 * is a class of bug users cannot see or explain.
 *
 * `toLowerCase`, not `toLocaleLowerCase`: the latter is locale-dependent and
 * turns `I` into a dotless `ı` under a Turkish locale, which would make the
 * canonical form depend on where the server happens to run.
 */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** Whether two addresses name the same mailbox. */
export function sameEmail(a: string, b: string): boolean {
  return normalizeEmail(a) === normalizeEmail(b);
}
